import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import ApiForm from './ApiForm';
import DatabaseDataSourceForm from './DatabaseDataSourceForm';
import { addDataSource, updateDataSource } from '../services/apiClient'; // Import updateDataSource
import { getErrorMessage } from '../utils/errors';
import { useAuth0 } from "@auth0/auth0-react";

export default function AddDataSourceModal({
    isOpen,
    onClose,
    tenantId,
    collectionId,
    agents, // Needed for DatabaseDataSourceForm's agent dropdown
    onSuccess, // Renamed from onSubmitSuccess
    dataSourceToEdit = null // Optional: DataSource object for editing
}) {
    const mode = dataSourceToEdit ? 'edit' : 'add';
    // Determine initial type based on edit mode, default to null for add mode
    const initialDataSourceType = mode === 'edit' ? (dataSourceToEdit.DataSourceType || dataSourceToEdit.data_source_type) : null;
    const [dataSourceType, setDataSourceType] = useState(initialDataSourceType);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const { getAccessTokenSilently } = useAuth0();

    // Reset state when modal closes or opens
    // Reset state when modal opens or closes, or when dataSourceToEdit changes
    useEffect(() => {
        if (isOpen) {
            const newType = mode === 'edit' ? (dataSourceToEdit.DataSourceType || dataSourceToEdit.data_source_type) : null;
            setDataSourceType(newType);
            setError(null);
            setIsLoading(false);
        } else {
            // Reset everything when closed
            setDataSourceType(null);
            setError(null);
            setIsLoading(false);
        }
    }, [isOpen, mode, dataSourceToEdit]);

    const handleApiSubmit = async (apiData) => {
        setIsLoading(true);
        setError(null);
        try {
            const token = await getAccessTokenSilently();
            // Prepare payload - structure needs to match ApiDataSource model exactly
            const payload = {
                name: apiData.name,
                endpoint: apiData.endpoint,
                http_method: apiData.method,
                description: apiData.description,
                auth_headers: apiData.authHeaders || {}, // Ensure it's an object
                // Construct request_config and response_schema from form fields
                request_config: {
                    fields: apiData.requestFields || [], // Use fields from ApiForm state
                    url_params: apiData.urlParamsEnabled || false
                },
                response_schema: {
                    fields: apiData.responseFields || [] // Use fields from ApiForm state
                },
                is_enabled: apiData.isEnabled !== undefined ? apiData.isEnabled : true,
                DataSourceType: 'API', // Set type
                // Add ID only if editing
                ...(mode === 'edit' && { DataSourceId: dataSourceToEdit.DataSourceId || dataSourceToEdit.data_source_id })
            };

            if (mode === 'edit') {
                console.log("Updating API DataSource:", payload);
                await updateDataSource(tenantId, collectionId, payload.DataSourceId, payload, token);
            } else {
                console.log("Adding API DataSource:", payload);
                // Remove ID for add operation if it somehow got included
                delete payload.DataSourceId;
                await addDataSource(tenantId, collectionId, payload, token);
            }

            onSuccess(); // Notify parent
            onClose(); // Close modal on success
        } catch (err) {
            console.error(`Failed to ${mode} API data source:`, err);
            setError(getErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    };

    // Database submit logic - supports both adding and editing
    const handleDatabaseSubmit = async (dbData) => {
        setIsLoading(true);
        setError(null);
        try {
            const token = await getAccessTokenSilently();
             // Prepare payload matching DatabaseDataSourceCreate model
             const payload = {
                 db_type: dbData.db_type,
                 db_host: dbData.db_host,
                 db_port: dbData.db_port,
                 db_name: dbData.db_name,
                 ...(dbData.db_schema ? { db_schema: dbData.db_schema } : {}),
                 db_user: dbData.db_user,
                 agent_id: dbData.agent_id,
                 is_enabled: dbData.is_enabled !== undefined ? dbData.is_enabled : true,
                 DataSourceType: 'DATABASE' // Use consistent casing
             };
             
            if (mode === 'edit') {
                // Update existing database data source
                const dataSourceId = dataSourceToEdit.DataSourceId || dataSourceToEdit.data_source_id;
                await updateDataSource(tenantId, collectionId, dataSourceId, payload, token);
            } else {
                // Add new database data source
                await addDataSource(tenantId, collectionId, payload, token);
            }
            
            onSuccess(); // Notify parent
            onClose(); // Close modal on success
        } catch (err) {
            console.error(`Failed to ${mode} Database data source:`, err);
            setError(getErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>
                        {mode === 'edit' 
                            ? `Edit ${dataSourceType === 'DATABASE' ? 'Database' : 'API'} Data Source`
                            : 'Add Data Source'
                        }
                    </DialogTitle>
                    <DialogDescription>
                        {mode === 'edit'
                            ? `Update the details for this ${dataSourceType === 'DATABASE' ? 'database' : 'API'} data source.`
                            : 'Choose the type of data source you want to add to this collection.'}
                    </DialogDescription>
                </DialogHeader>

                {error && <p className="text-sm text-red-600 px-6 pb-4 -mt-2">{error}</p>}

                {/* Show type selection only in 'add' mode */}
                {mode === 'add' && !dataSourceType && (
                    <div className="p-6 grid grid-cols-2 gap-4">
                        <Button variant="outline" onClick={() => setDataSourceType('API')}>
                            API Endpoint
                        </Button>
                        <Button variant="outline" onClick={() => setDataSourceType('DATABASE')}>
                            SQL Database
                        </Button>
                    </div>
                )}

                {/* Show API form if type is API (in add or edit mode) */}
                {dataSourceType === 'API' && (
                    <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                        <ApiForm
                            tenantId={tenantId}
                            collectionId={collectionId}
                            // Pass existing data if editing, otherwise empty object
                            initialData={mode === 'edit' ? dataSourceToEdit : {}}
                            onSubmit={handleApiSubmit} // Form calls this on submit
                            // Go back only if in add mode, otherwise just close
                            onCancel={mode === 'add' ? () => setDataSourceType(null) : onClose}
                            isEditing={mode === 'edit'} // Pass mode to ApiForm if needed
                            isLoading={isLoading} // Pass loading state
                        />
                    </div>
                )}

                {/* Show DB form if type is DATABASE (both add and edit modes) */}
                {dataSourceType === 'DATABASE' && (
                    <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                        <DatabaseDataSourceForm
                            agents={agents || []}
                            initialData={mode === 'edit' ? dataSourceToEdit : null}
                            onSubmit={handleDatabaseSubmit}
                            onCancel={() => mode === 'add' ? setDataSourceType(null) : onClose()}
                            isLoading={isLoading}
                        />
                    </div>
                )}

                {/* Show footer with Cancel button only during initial type selection in add mode */}
                {mode === 'add' && !dataSourceType && (
                     <DialogFooter>
                         <Button variant="ghost" onClick={onClose}>Cancel</Button>
                     </DialogFooter>
                 )}
            </DialogContent>
        </Dialog>
    );
}
